import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert,
} from '@mui/material';
import { CheckCircle, Cancel, Refresh, HealthAndSafety } from '@mui/icons-material';
import { supabase } from '../lib/supabase';

interface HealthCheck {
  name: string;
  status: 'healthy' | 'unhealthy' | 'checking';
  responseTime?: number;
  error?: string;
}

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || 'https://m7dxffi3e9.ap-southeast-1.awsapprunner.com';

const SystemHealthPage: React.FC = () => {
  const [checks, setChecks] = useState<HealthCheck[]>([
    { name: 'Backend API', status: 'checking' },
    { name: 'Supabase Database', status: 'checking' },
    { name: 'YouTube Data API', status: 'checking' },
  ]);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const updateCheck = (name: string, update: Partial<HealthCheck>) => {
    setChecks(prev => prev.map(c => c.name === name ? { ...c, ...update } : c));
  };

  const runChecks = useCallback(async () => {
    setChecks(prev => prev.map(c => ({ ...c, status: 'checking' as const })));

    // Backend API
    try {
      const start = Date.now();
      const res = await fetch(`${BACKEND_API_URL}/health`, { signal: AbortSignal.timeout(10000) });
      const elapsed = Date.now() - start;
      updateCheck('Backend API', {
        status: res.ok ? 'healthy' : 'unhealthy',
        responseTime: elapsed,
        error: res.ok ? undefined : `HTTP ${res.status}`,
      });
    } catch (err: any) {
      updateCheck('Backend API', { status: 'unhealthy', error: err.message || 'Connection failed' });
    }

    // Supabase
    try {
      const start = Date.now();
      const { error } = await supabase.from('profiles').select('id').limit(1);
      const elapsed = Date.now() - start;
      updateCheck('Supabase Database', {
        status: error ? 'unhealthy' : 'healthy',
        responseTime: elapsed,
        error: error?.message,
      });
    } catch (err: any) {
      updateCheck('Supabase Database', { status: 'unhealthy', error: err.message || 'Connection failed' });
    }

    // YouTube API
    const ytKey = process.env.REACT_APP_YOUTUBE_API_KEY;
    if (!ytKey) {
      updateCheck('YouTube Data API', { status: 'unhealthy', error: 'API key not configured' });
    } else {
      try {
        const start = Date.now();
        const res = await fetch(`https://www.googleapis.com/youtube/v3/videos?part=id&id=dQw4w9WgXcQ&key=${ytKey}`, { signal: AbortSignal.timeout(10000) });
        const elapsed = Date.now() - start;
        updateCheck('YouTube Data API', {
          status: res.ok ? 'healthy' : 'unhealthy',
          responseTime: elapsed,
          error: res.ok ? undefined : `HTTP ${res.status}`,
        });
      } catch (err: any) {
        updateCheck('YouTube Data API', { status: 'unhealthy', error: err.message || 'Connection failed' });
      }
    }

    setLastChecked(new Date());
  }, []);

  useEffect(() => {
    runChecks();
  }, [runChecks]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(runChecks, 60000);
    return () => clearInterval(interval);
  }, [autoRefresh, runChecks]);

  const allHealthy = checks.every(c => c.status === 'healthy');
  const anyChecking = checks.some(c => c.status === 'checking');

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <HealthAndSafety color={allHealthy ? 'success' : 'error'} fontSize="large" />
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">System Health</Typography>
            <Typography variant="body2" color="text.secondary">
              {lastChecked ? `Last checked: ${lastChecked.toLocaleTimeString()}` : 'Checking...'}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant={autoRefresh ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={runChecks} disabled={anyChecking}>
            Refresh
          </Button>
        </Box>
      </Box>

      {allHealthy && !anyChecking && (
        <Alert severity="success" sx={{ mb: 3 }}>All systems are operational.</Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {checks.map(check => (
          <Grid item xs={12} md={4} key={check.name}>
            <Card variant="outlined" sx={{ borderColor: check.status === 'healthy' ? 'success.main' : check.status === 'unhealthy' ? 'error.main' : 'divider', borderWidth: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                {check.status === 'checking' ? (
                  <CircularProgress size={40} sx={{ mb: 1 }} />
                ) : check.status === 'healthy' ? (
                  <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                ) : (
                  <Cancel color="error" sx={{ fontSize: 40, mb: 1 }} />
                )}
                <Typography variant="h6" fontWeight="bold">{check.name}</Typography>
                <Chip
                  label={check.status === 'checking' ? 'Checking...' : check.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
                  color={check.status === 'healthy' ? 'success' : check.status === 'unhealthy' ? 'error' : 'default'}
                  size="small"
                  sx={{ mt: 1 }}
                />
                {check.responseTime && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{check.responseTime}ms</Typography>
                )}
                {check.error && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>{check.error}</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>Service Details</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Endpoint</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Response Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Backend API</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{BACKEND_API_URL}/health</TableCell>
                  <TableCell align="center"><Chip label={checks[0].status} size="small" color={checks[0].status === 'healthy' ? 'success' : checks[0].status === 'unhealthy' ? 'error' : 'default'} /></TableCell>
                  <TableCell align="right">{checks[0].responseTime ? `${checks[0].responseTime}ms` : '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Supabase</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{process.env.REACT_APP_SUPABASE_URL}</TableCell>
                  <TableCell align="center"><Chip label={checks[1].status} size="small" color={checks[1].status === 'healthy' ? 'success' : checks[1].status === 'unhealthy' ? 'error' : 'default'} /></TableCell>
                  <TableCell align="right">{checks[1].responseTime ? `${checks[1].responseTime}ms` : '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>YouTube Data API</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>googleapis.com/youtube/v3</TableCell>
                  <TableCell align="center"><Chip label={checks[2].status} size="small" color={checks[2].status === 'healthy' ? 'success' : checks[2].status === 'unhealthy' ? 'error' : 'default'} /></TableCell>
                  <TableCell align="right">{checks[2].responseTime ? `${checks[2].responseTime}ms` : '-'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SystemHealthPage;
